import * as React from "react";
import { IntlShape } from "react-intl";
import MoneyAmount from "../components/MoneyAmount";
import type { ArticlePrices, Price } from "../model/Prices";
import type { Money, MoneyWrapper, PriceConfigured, PositionPrices, PriceConfiguration, UnitPrices } from "./BaseTypes";
export function getConfiguredPrice(
	prices: {
		net: Money | null | undefined;
		gross: Money | null | undefined;
	},
	priceConfiguration: PriceConfiguration | null | undefined,
): Money | null | undefined {
	if (!priceConfiguration) {
		return null;
	}

	const priceType = priceConfiguration.priceBreakdown.primaryPriceType;

	if (priceType === "gross") {
		return prices.gross;
	}

	return prices.net;
}
export function getConfiguredTotalPrice(
	priceWithoutMarginTax: Price,
	priceWithMarginTax: Price | null | undefined,
	priceConfiguration: PriceConfiguration | null | undefined,
): Money | null | undefined {
	if (!priceConfiguration) {
		return null;
	}

	const relevantPrice = priceConfiguration.priceBreakdown.includeMarginTaxesInPrice
		? priceWithMarginTax
		: priceWithoutMarginTax;
	return priceConfiguration.priceBreakdown.primaryPriceType === "gross"
		? relevantPrice && relevantPrice.gross
		: relevantPrice && relevantPrice.net;
}

function getPositionPrices(
	priceIsGross: boolean,
	articlePrices: ArticlePrices,
	priceConfiguration: PriceConfiguration,
): PositionPrices {
	const hideInfo = priceConfiguration.priceCompact.primary === "unitGrossPriceOnly";
	const noSecondary = !priceConfiguration.priceCompact.secondary;
	const includeMarginTax = priceConfiguration.priceBreakdown.includeMarginTaxesInPrice;
	const priceWithTax = articlePrices.position.articleWithTaxes;
	const gross = {
		amount: includeMarginTax && priceWithTax ? priceWithTax.gross : articlePrices.position.article.gross,
		strikeThrough: articlePrices.position.strikeThrough.gross,
		info: hideInfo ? null : "ORC_ARTICLE_INCL_VAT",
	};
	const net = {
		amount: includeMarginTax && priceWithTax ? priceWithTax.net : articlePrices.position.article.net,
		strikeThrough: articlePrices.position.strikeThrough.net,
		info: hideInfo ? null : "ORC_ARTICLE_EXCL_VAT",
	};
	const primary = priceIsGross ? gross : net;
	const secondary = priceIsGross ? net : gross;
	return {
		primary: primary,
		secondary: noSecondary ? null : secondary,
	};
}

function getUnitPrices(
	priceIsGross: boolean,
	articlePrices: ArticlePrices,
	priceConfiguration: PriceConfiguration,
): UnitPrices {
	const hideInfo = priceConfiguration.priceCompact.primary === "unitGrossPriceOnly";
	const includeMarginTax = priceConfiguration.priceBreakdown.includeMarginTaxesInPrice;
	const priceWithTax = articlePrices.bundle.articleWithTaxes;
	const noSecondary = !priceConfiguration.priceCompact.secondary;
	const bundleNet = {
		amount: includeMarginTax && priceWithTax ? priceWithTax.net : articlePrices.bundle.article.net,
		strikeThrough: articlePrices.bundle.strikeThrough.net,
		info: hideInfo ? null : "ORC_ARTICLE_EXCL_VAT",
	};
	const bundleGross = {
		amount: includeMarginTax && priceWithTax ? priceWithTax.gross : articlePrices.bundle.article.gross,
		strikeThrough: articlePrices.bundle.strikeThrough.gross,
		info: hideInfo ? null : "ORC_ARTICLE_INCL_VAT",
	};
	const empties = getEmptiesPrice(priceIsGross, articlePrices);
	return {
		primary: priceIsGross ? bundleGross : bundleNet,
		secondary: noSecondary ? null : priceIsGross ? bundleNet : bundleGross,
		empties: empties,
	};
}

function getEmptiesPrice(priceIsGross: boolean, articlePrices: ArticlePrices): MoneyWrapper {
	if (priceIsGross) {
		return {
			amount: articlePrices.bundle.empties.gross,
			strikeThrough: null,
			info: "ORC_SHOPPINGCARTPAGE_PRICE_DEPOSITS",
		};
	}

	return {
		amount: articlePrices.bundle.empties.net,
		strikeThrough: null,
		info: "ORC_SHOPPINGCARTPAGE_PRICE_DEPOSITS",
	};
}

function getCurrency(articlePrices: ArticlePrices): string {
	if (articlePrices.single.article.net) {
		return articlePrices.single.article.net.currency;
	}

	if (articlePrices.bundle.article.net) {
		return articlePrices.bundle.article.net.currency;
	}

	return "";
}

export function processConfig(
	articlePrices: ArticlePrices | null | undefined,
	priceConfiguration: PriceConfiguration | null | undefined,
): PriceConfigured {
	if (!articlePrices || !priceConfiguration) {
		return {
			valid: false,
		};
	}

	const priceIsGross = priceConfiguration.priceBreakdown.primaryPriceType === "gross";
	return {
		valid: true,
		currency: getCurrency(articlePrices),
		employeePriceIsGross: priceIsGross,
		unitPrices: getUnitPrices(priceIsGross, articlePrices, priceConfiguration),
		positionPrices: getPositionPrices(priceIsGross, articlePrices, priceConfiguration),
		savings: articlePrices.savings,
		summaryDnr: articlePrices.summaryDnr,
	};
}
export function withOptionalWeightAddition(
	price: Money | null | undefined,
	perKg: boolean | null | undefined,
	intl: IntlShape,
) {
	const unitAddition = perKg
		? " / " +
			intl.formatMessage({
				id: "ORC_SHOPPINGCARTPAGE_UPLOAD_SEARCH_WEIGHTED",
			})
		: undefined;
	return <MoneyAmount amount={price} unitAddition={unitAddition} />;
}
export function getAmountInCents(amountWithTwoDecimalPlaces: number): number {
	return Math.ceil(amountWithTwoDecimalPlaces * 100);
}
